import MenuIcon from "@redotech/redo-icons/menu-01_filled.svg";
import SearchIcon from "@redotech/redo-icons/search-md.svg";
import ShoppingCartIcon from "@redotech/redo-icons/shopping-cart-01.svg";
import { useCampaign } from "@redotech/redo-marketplace-app-common/campaign-context";
import {
  SetVendorAuthContext,
  VendorAuthContext,
} from "@redotech/redo-marketplace-app-common/vendor-auth";
import { RedoButton } from "@redotech/redo-web/arbiter-components/buttons/redo-button";
import { RedoTextInput } from "@redotech/redo-web/arbiter-components/input/redo-text-input";
import RedoLogoSvg from "@redotech/redo-web/logo.svg";
import { memo, useContext, useState } from "react";
import { Link, useNavigate } from "react-router-dom";

interface SearchInputProps {
  value?: string;
  onChange?: (value: string) => void;
  placeholder?: string;
}

const SearchInput = memo(function SearchInput({
  value,
  onChange,
  placeholder = "Search",
}: SearchInputProps) {
  const [internalValue, setInternalValue] = useState(value || "");

  const handleChange = (newValue: string) => {
    setInternalValue(newValue);
    onChange?.(newValue);
  };

  return (
    <RedoTextInput
      IconLeading={SearchIcon}
      placeholder={placeholder}
      setValue={handleChange}
      size="sm"
      value={value !== undefined ? value : internalValue}
    />
  );
});

export interface MarketplaceNavbarShadcnProps {
  onSearchChange?: (value: string) => void;
  onSignUpClick?: () => void;
  onCartClick?: () => void;
  onBrandsClick?: () => void;
  onProductsClick?: () => void;
  onCategoriesClick?: () => void;
  onProductSubMenuClick?: (category: string) => void;
  searchValue?: string;
  searchPlaceholder?: string;
  cartItemCount?: number;
}

export const MarketplaceNavbarShadcn = memo(function MarketplaceNavbarShadcn({
  onSearchChange,
  onSignUpClick,
  onCartClick,
  onBrandsClick,
  onProductsClick,
  onCategoriesClick,
  onProductSubMenuClick,
  searchValue,
  searchPlaceholder = "Search",
  cartItemCount = 0,
}: MarketplaceNavbarShadcnProps) {
  const navigate = useNavigate();
  const vendorAuth = useContext(VendorAuthContext);
  const setVendorAuth = useContext(SetVendorAuthContext);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const { campaign } = useCampaign();
  const handleLogin = () => {
    navigate("/login");
    setIsMobileMenuOpen(false);
  };

  const handleLogout = () => {
    if (setVendorAuth) {
      setVendorAuth(undefined);
    }
    setIsMobileMenuOpen(false);

    // Redirect to campaign login if there's an active campaign, otherwise go to home
    if (campaign?.slug) {
      navigate(`/${campaign.slug}`);
    } else {
      navigate("/");
    }
  };

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  const closeMobileMenu = () => {
    setIsMobileMenuOpen(false);
  };

  return (
    <nav className="sticky top-0 z-50 bg-white">
      <div className="px-4 xl:px-8 py-4">
        {/* Desktop and mobile header */}
        <div className="flex items-center justify-between h-16">
          {/* Logo and Desktop Navigation */}
          <div className="grid grid-cols-[auto_1fr] items-center gap-6">
            <Link className="flex items-center" to="/">
              {campaign?.branding?.logoUrl ? (
                <img
                  alt={campaign.name}
                  className="block h-8 w-auto object-contain"
                  src={campaign.branding.logoUrl}
                />
              ) : (
                <RedoLogoSvg className="block h-6 w-auto" />
              )}
            </Link>

            {/* Desktop Navigation - hidden on mobile */}
            {/* {!campaign && (
              <div className="h-6 hidden md:flex items-center gap-6">
                <Link
                  className="text-xs font-semibold uppercase leading-none"
                  onClick={closeMobileMenu}
                  to="/brands"
                >
                  BRANDS
                </Link>
                <Link
                  className="text-xs font-semibold uppercase leading-none"
                  onClick={closeMobileMenu}
                  to="/"
                >
                  PRODUCTS
                </Link>
              </div>
            )} */}
          </div>

          {/* Desktop Right Side - Search, Auth, Cart */}
          <div className="hidden md:flex items-center gap-4">
            <SearchInput
              onChange={onSearchChange}
              placeholder={searchPlaceholder}
              value={searchValue}
            />

            {vendorAuth ? (
              <RedoButton
                hierarchy="primary"
                onClick={handleLogout}
                text="Sign out"
              />
            ) : (
              <RedoButton
                hierarchy="primary"
                onClick={handleLogin}
                text="Login"
              />
            )}

            <div className="relative">
              <RedoButton
                dataTestId="cart-button"
                IconLeading={ShoppingCartIcon}
                onClick={onCartClick}
              />
              {cartItemCount > 0 && (
                <div className="absolute -top-2 -right-2 bg-red-500 text-white text-xs font-bold rounded-full h-5 w-5 flex items-center justify-center">
                  {cartItemCount > 99 ? "99+" : cartItemCount}
                </div>
              )}
            </div>
          </div>

          {/* Mobile Right Side - Cart and Menu Button */}
          <div className="flex md:hidden items-center gap-2">
            <div className="relative">
              <RedoButton
                dataTestId="cart-button"
                IconLeading={ShoppingCartIcon}
                onClick={onCartClick}
              />
              {cartItemCount > 0 && (
                <div className="absolute -top-2 -right-2 bg-red-500 text-white text-xs font-bold rounded-full h-5 w-5 flex items-center justify-center">
                  {cartItemCount > 99 ? "99+" : cartItemCount}
                </div>
              )}
            </div>

            {/* Mobile menu toggle button */}
            <RedoButton
              dataTestId="mobile-menu-button"
              IconLeading={MenuIcon}
              onClick={toggleMobileMenu}
            />
          </div>
        </div>

        {/* Mobile Menu - shown when isMobileMenuOpen is true */}
        {isMobileMenuOpen && (
          <div className="md:hidden bg-white border-t border-gray-200">
            <div className="flex flex-col space-y-4 py-4">
              {/* Mobile Search */}
              <div className="px-2">
                <SearchInput
                  onChange={onSearchChange}
                  placeholder={searchPlaceholder}
                  value={searchValue}
                />
              </div>

              {/* Mobile Navigation Links */}
              {!campaign && (
                <>
                  <Link
                    className="px-2 py-2 text-sm font-semibold uppercase hover:bg-gray-50"
                    onClick={closeMobileMenu}
                    to="/brands"
                  >
                    Brands
                  </Link>
                  <Link
                    className="px-2 py-2 text-sm font-semibold uppercase hover:bg-gray-50"
                    onClick={closeMobileMenu}
                    to="/"
                  >
                    Products
                  </Link>
                </>
              )}

              {/* Mobile Auth Button */}
              <div className="px-2">
                {vendorAuth ? (
                  <RedoButton
                    hierarchy="primary"
                    onClick={handleLogout}
                    text="Sign out"
                  />
                ) : (
                  <RedoButton
                    hierarchy="primary"
                    onClick={handleLogin}
                    text="Login"
                  />
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </nav>
  );
});
